/**
 * useClientLevelData Hook
 * TASK-281: Client Level View - Multi-Portfolio Comparison
 *
 * Returns client-level data aggregated across all portfolios.
 * Uses real backend API calls - NO MOCK FALLBACK.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portfolioApiService, StressTests, PerformanceAttribution } from '../services/api/portfolioApiService';
import { ClientFilters } from '../types/portfolio-views';

// ============================================================================
// TypeScript Interfaces for Client-Level Data
// ============================================================================

export interface ClientInfo {
  id: string;
  name: string;
  totalAuM: number;
  healthScore: number | null;
  lastContact: string | null;
  nextMeeting?: string;
  riskProfile: string | null;
}

export interface PortfolioInfo {
  id: string;
  name: string;
  value: number;
  percentOfClientAuM: number;
  mandateType: string;
  riskProfile: string | null;
  performance: number | null;
  accountCount: number;
  accounts: string[];
  currency: string;
  alerts?: string[];
}

export interface ConsolidatedPosition {
  isin: string;
  name: string;
  quantity: number;
  price: number;
  value: number;
  asset_class: string;
  currency: string;
  country?: string;
  sector?: string;
  unrealized_pl: number;
  unrealized_pl_pct: number;
  portfolios: string[];
  breakdown?: Array<{
    portfolio: string;
    value: number;
    percentage: number;
  }>;
  percentOfTotal: number;
  concentrationRisk?: string;
  isDuplicate?: boolean;
}

export interface DuplicateHolding {
  isin: string;
  name: string;
  portfolios: string[];
  totalValue: number;
  percentOfTotal: number;
  breakdown: Array<{
    portfolio: string;
    name: string;
    value: number;
  }>;
}

export interface ConcentrationRisk {
  name: string;
  value: number;
  percentOfTotal: number;
  riskLevel: string;
  portfolios: string[];
}

export interface AllocationTarget {
  asset_class: string;
  target_weight: number;
  current_weight: number;
}

export interface ConsolidatedHoldings {
  totalValue: number;
  positions: ConsolidatedPosition[];
  assetAllocation: Record<string, number>;
  geographicExposure: Record<string, number>;
  /** TASK-874: what kind of place each `geographicExposure` key is. See AssetAllocation. */
  geographicTiers: Record<string, 'country' | 'region' | 'unresolved' | 'unknown'>;
  sectorExposure: Record<string, number>;
  currencyExposure: Record<string, number>;
  duplicates: DuplicateHolding[];
  concentration: ConcentrationRisk[];
  allocationTargets?: AllocationTarget[];
}

export interface DriftAnalysis {
  driftPercentage: number | null;
  currentAllocation: Record<string, number>;
  targetAllocation: Record<string, number>;
  driftByAssetClass: Record<string, {
    current: number;
    target: number;
    drift: number;
    driftValue: number;
    status: string;
  }>;
  /**
   * `null` = the drift could NOT be assessed, which is not the same answer as "no rebalancing
   * needed". TASK-723 widened the API contract to `boolean | null` for exactly this reason
   * ("NEVER read null as 'no rebalancing needed'", `portfolioApiService.ts:910`) and this type
   * stayed `boolean`, which is what broke the build. Coercing with `?? false` would have
   * silenced tsc by telling every reader that an unassessable portfolio is fine.
   */
  needsRebalancing: boolean | null;
  rebalanceUrgency: string | null;
  suggestedTrades: Array<{
    assetClass: string;
    action: 'BUY' | 'SELL';
    amount: number;
    amountPercentage: number;
    reason: string;
    priority: string;
  }>;
  riskProfile: string | null;
  error?: string;
}

export interface ClientInsights {
  concentrationRisks: Array<{
    type: string;
    severity: string;
    title: string;
    description: string;
    value: number;
    percentage: number;
    recommendation: string;
  }>;
  cashPosition: {
    total: number;
    percentage: number;
    breakdown: Array<{
      portfolio: string;
      amount: number;
      percentage: number;
    }>;
    opportunityCost?: number;
    recommendation?: string;
  };
  diversificationGaps: Array<{
    type: string;
    region?: string;
    sector?: string;
    currentExposure: number;
    recommendedExposure: number;
    description: string;
  }>;
  currencyRisk?: {
    usdExposure: number;
    fxImpactScenario?: {
      usdChfDrop10Pct: number;
      currentHedge: number;
      recommendation?: string;
    };
  };
  liquidityPosition?: {
    immediate: number;
    tPlus2: number;
    lowLiquidity: number;
  };
}

export interface PerformanceData {
  combined: {
    mtd: number | null;
    qtd: number | null;
    ytd: number | null;
    oneYear: number | null;
    sinceStart: number | null;
  };
  benchmark: {
    mtd: number | null;
    qtd: number | null;
    ytd: number | null;
    oneYear: number | null;
    sinceStart: number | null;
  } | null;
  benchmarkName: string | null;
  benchmarkCode: string | null;
  alpha: {
    mtd: number | null;
    qtd: number | null;
    ytd: number | null;
    oneYear: number | null;
    sinceStart: number | null;
  } | null;
  byPortfolio: Array<{
    id: string;
    name: string;
    ytd: number | null;
    contributionToTotal: number | null;
    volatility: number | null;
  }>;
  topContributors: Array<{
    name: string;
    gain: number;
    percentage: number;
  }>;
  bottomContributors?: Array<{
    name: string;
    contribution: number;
    percentage: number;
  }>;
  history?: Array<{
    date: string;
    value: number;
    benchmark?: number;
    [key: string]: any;
  }>;
  totalGains: number | null; // TASK-654: unavailable is null, never a fake 0
}

export interface RiskData {
  combined: {
    volatility: number | null;
    var1day: number | null;
    sharpeRatio: number | null;
    maxDrawdown: number | null;
    beta: number | null;
  };
  byPortfolio: Array<{
    id: string;
    name: string;
    volatility: number | null;
    var1day: number | null;
    sharpeRatio: number | null;
  }>;
}

export interface TaxEfficiencyData {
  taxLossOpportunities: any[];
  gainCoordinationStrategies: any[];
  dividendRecommendations: any[];
  estimatedTotalTaxSavings: number;
}

export interface ClientLevelData {
  client: ClientInfo;
  portfolios: PortfolioInfo[];
  consolidatedHoldings: ConsolidatedHoldings;
  drift: DriftAnalysis | null;
  insights: ClientInsights;
  performance: PerformanceData;
  risk: RiskData;
  stressTests: StressTests | null;
  attribution: PerformanceAttribution | null;
  /**
   * TASK-722: OPTIONAL and currently never assigned by any backend. `undefined` therefore
   * means "no optimization analysis has run", which is NOT the same as an empty array
   * ("it ran and found nothing"). Consumers must not collapse the two — see
   * `PortfolioOptimizationSuggestions.analysisStatus`.
   */
  optimizationScenarios?: any[];
  taxEfficiency?: TaxEfficiencyData;
  correlationMatrix?: any;
}

// ============================================================================
// Hook Implementation
// ============================================================================

interface UseClientLevelDataReturn {
  data: ClientLevelData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Axios rejects with the response attached; httpClient does not unwrap it. */
const isNotFound = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 404;

/**
 * Fetch client-level data with filters applied
 * Uses real backend API calls - NO MOCK FALLBACK
 *
 * @param clientId - Client ID to fetch data for
 * @param filters - Client-level filters from portfolioStore
 * @returns data, loading state, error state, and refetch function
 */
export function useClientLevelData(
  clientId: string,
  filters: ClientFilters,
  benchmark: string = 'SPI',
  periodOverride?: '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all'
): UseClientLevelDataReturn {
  const queryClient = useQueryClient();

  // Map dateMode to API period parameter (can be overridden by explicit period)
  const period: '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all' =
    periodOverride ?? (filters.dateMode === 'historical' ? 'all' : 'ytd');

  // Main client data query — pass filters to API
  const mainQuery = useQuery({
    queryKey: ['client-dashboard', clientId, period, benchmark, filters],
    queryFn: async () => {
      if (!clientId) return null;

      // Step 1: Fetch main dashboard data + portfolio list in parallel
      const [dashboardData, portfoliosData] = await Promise.all([
        portfolioApiService.getClientDashboardData(clientId, period, undefined, benchmark),
        portfolioApiService.getClientPortfolios(clientId),
      ]);

      // Step 2: Fetch per-portfolio performance history in parallel
      const portfolioHistories = await Promise.allSettled(
        portfoliosData.portfolios.map((p) =>
          portfolioApiService.getPerformance(clientId, period, p.id, benchmark)
        )
      );

      // Build map: portfolioId → history[]
      const perPortfolioHistory = new Map<string, any[]>();
      portfoliosData.portfolios.forEach((p, i) => {
        const result = portfolioHistories[i];
        if (result.status === 'fulfilled' && result.value?.history) {
          perPortfolioHistory.set(p.id, result.value.history);
        }
      });

      return { dashboardData, portfoliosData, perPortfolioHistory };
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // A 404 is a settled answer (no such client) — retrying it just delays the empty state.
    retry: (failureCount, err) => !isNotFound(err) && failureCount < 2,
  });

  // Drift data query (separate because it can fail if no risk profile)
  const driftQuery = useQuery({
    queryKey: ['client-drift', clientId],
    queryFn: () => portfolioApiService.getDrift(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    retry: 1, // Less retries since it may fail due to missing risk profile
  });

  // Combine loading states
  const loading = mainQuery.isLoading;

  // Error handling - NO MOCK FALLBACK.
  // A 404 is not a transport failure: the client does not exist (stale link, deleted client,
  // or a portfolio id in the :clientId slot). Leaving `error` null lets the view fall through
  // to its "Client Not Found" empty state instead of blaming the user's connection.
  const error = mainQuery.error && !isNotFound(mainQuery.error)
    ? 'Failed to load client data. Please check your connection and try again.'
    : null;

  // Transform API response to ClientLevelData structure
  const data: ClientLevelData | null = mainQuery.data
    ? transformToClientLevelData(
        mainQuery.data.dashboardData,
        mainQuery.data.portfoliosData,
        driftQuery.data,
        driftQuery.error,
        mainQuery.data.perPortfolioHistory
      )
    : null;

  // Refetch function
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['client-dashboard', clientId] });
    queryClient.invalidateQueries({ queryKey: ['client-drift', clientId] });
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
}

// ============================================================================
// Data Transformation Functions
// ============================================================================

function transformToClientLevelData(
  dashboardData: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>,
  portfoliosData: Awaited<ReturnType<typeof portfolioApiService.getClientPortfolios>>,
  driftData: Awaited<ReturnType<typeof portfolioApiService.getDrift>> | undefined,
  driftError: unknown,
  perPortfolioHistory?: Map<string, any[]>
): ClientLevelData {
  const { overview, positions, allocation, performance, performanceHistory, risk, stressTests, cash, totalCashCHF, attribution } = dashboardData;
  const { portfolios } = portfoliosData;

  // Calculate per-portfolio values from positions (needed for AuM fallback and portfolio breakdown)
  const portfolioValueMap = new Map<string, { value: number; unrealizedPl: number }>();
  positions.forEach((pos) => {
    const portfolioId = pos.portfolio_id || 'default';
    const existing = portfolioValueMap.get(portfolioId) || { value: 0, unrealizedPl: 0 };
    // TASK-865 review: per-portfolio value must be converted too, or every "% of AuM"
    // computed from it is a converted numerator over a par denominator.
    existing.value += pos.market_value_base ?? 0;
    existing.unrealizedPl += pos.unrealized_pl || 0;
    portfolioValueMap.set(portfolioId, existing);
  });

  // Calculate total AuM — use the larger of overview vs positions sum
  // (overview.total_value can be stale or only reflect one portfolio)
  //
  // TASK-865 review: this summed RAW `market_value`, so once `overview.total_value` became
  // CHF-converted the par sum was reliably the LARGER of the two and `Math.max` picked it
  // every time — silently discarding the conversion on this whole view, and making every
  // percentage below a converted numerator over a par denominator. The `Math.max` guard is
  // about STALENESS, not units; both sides must be one currency for it to mean anything.
  const positionsTotalValue = positions.reduce((sum, p) => sum + (p.market_value_base ?? 0), 0);
  const computedTotal = positionsTotalValue + (totalCashCHF || 0);
  const totalAuM = Math.max(overview.total_value || 0, computedTotal);

  // Resolve client name: overview → first portfolio's client name → clientId
  const resolvedClientName = overview.client_name
    || (portfolios.length > 0 ? (portfolios[0] as any).client_name : undefined)
    || overview.client_id;

  const client: ClientInfo = {
    id: overview.client_id,
    name: resolvedClientName,
    totalAuM,
    healthScore: overview.health_score ?? null,
    lastContact: null,
    riskProfile: driftData?.drift?.risk_profile ?? null,
  };

  // Transform portfolios with calculated values
  const portfolioInfos: PortfolioInfo[] = portfolios.map((p) => {
    const data = portfolioValueMap.get(p.id) || { value: 0, unrealizedPl: 0 };
    const percentOfAuM = totalAuM > 0 ? (data.value / totalAuM) * 100 : 0;
    // Approximate performance from unrealized P&L
    const performance = data.value > 0 ? (data.unrealizedPl / data.value) * 100 : null;

    return {
      id: p.id,
      name: p.name,
      value: data.value,
      percentOfClientAuM: percentOfAuM,
      mandateType: p.portfolio_type || 'investment',
      riskProfile: null,
      performance,
      accountCount: 1,
      accounts: [],
      currency: p.currency,
    };
  });

  // Transform consolidated holdings
  const consolidatedHoldings = transformHoldings(positions, allocation, totalAuM, cash, totalCashCHF);

  // Transform drift data
  const drift = transformDrift(driftData, driftError);

  // Generate insights from data
  const insights = generateInsights(consolidatedHoldings, cash, totalCashCHF, totalAuM, portfolios);

  // Transform performance data with breakdown and history
  // TASK-387: Pass backend-calculated contributors
  const backendContributors = {
    top: (dashboardData as any).topContributors ?? [],
    bottom: (dashboardData as any).bottomContributors ?? [],
  };
  const performanceData = transformPerformance(performance, overview, positions, portfolios, totalAuM, performanceHistory, perPortfolioHistory, backendContributors);

  // Transform risk data with breakdown
  const riskData = transformRisk(risk, overview, positions, portfolios, totalAuM);

  return {
    client,
    portfolios: portfolioInfos,
    consolidatedHoldings,
    drift,
    insights,
    performance: performanceData,
    risk: riskData,
    stressTests: stressTests || null,
    attribution: attribution || null,
    correlationMatrix: dashboardData.correlationMatrix ?? undefined,
  };
}

function transformHoldings(
  positions: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['positions'],
  allocation: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['allocation'],
  totalAuM: number,
  _cash: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['cash'],
  _totalCashCHF: number
): ConsolidatedHoldings {
  // Group positions by ISIN to find duplicates
  const positionsByIsin = new Map<string, typeof positions>();
  positions.forEach((pos) => {
    const existing = positionsByIsin.get(pos.isin) || [];
    existing.push(pos);
    positionsByIsin.set(pos.isin, existing);
  });

  // Transform positions
  const consolidatedPositions: ConsolidatedPosition[] = [];
  const duplicates: DuplicateHolding[] = [];
  const concentration: ConcentrationRisk[] = [];

  positionsByIsin.forEach((posArray, isin) => {
    const totalValue = posArray.reduce((sum, p) => sum + (p.market_value || 0), 0);
    const totalQuantity = posArray.reduce((sum, p) => sum + (p.quantity || 0), 0);
    const percentOfTotal = totalAuM > 0 ? (totalValue / totalAuM) * 100 : 0;

    // Determine concentration risk
    let concentrationRisk: string | undefined;
    if (percentOfTotal > 20) concentrationRisk = 'HIGH';
    else if (percentOfTotal > 10) concentrationRisk = 'MEDIUM';

    // Use portfolio_id (not position id) and deduplicate
    const portfolioIds = [...new Set(posArray.map((p) => p.portfolio_id || p.id))];
    const isDuplicate = portfolioIds.length > 1;

    const position: ConsolidatedPosition = {
      isin,
      name: posArray[0].security_name,
      quantity: totalQuantity,
      price: posArray[0].market_price || 0,
      value: totalValue,
      asset_class: posArray[0].asset_class || 'Other',
      currency: posArray[0].currency,
      country: posArray[0].country || undefined,
      sector: posArray[0].sector,
      unrealized_pl: posArray.reduce((sum, p) => sum + (p.unrealized_pl || 0), 0),
      unrealized_pl_pct: 0, // Calculate if book_value available
      portfolios: portfolioIds,
      percentOfTotal: Math.round(percentOfTotal * 100) / 100,
      concentrationRisk,
      isDuplicate,
    };

    if (isDuplicate) {
      // Group breakdown by portfolio (aggregate positions within the same portfolio)
      const breakdownMap = new Map<string, { value: number; name: string }>();
      posArray.forEach((p) => {
        const pId = p.portfolio_id || p.id;
        const existing = breakdownMap.get(pId) || { value: 0, name: p.security_name };
        existing.value += p.market_value || 0;
        breakdownMap.set(pId, existing);
      });

      position.breakdown = Array.from(breakdownMap.entries()).map(([portfolio, data]) => ({
        portfolio,
        value: data.value,
        percentage: totalAuM > 0 ? (data.value / totalAuM) * 100 : 0,
      }));

      duplicates.push({
        isin,
        name: posArray[0].security_name,
        portfolios: portfolioIds,
        totalValue,
        percentOfTotal,
        breakdown: Array.from(breakdownMap.entries()).map(([portfolio, data]) => ({
          portfolio,
          name: data.name,
          value: data.value,
        })),
      });
    }

    if (concentrationRisk) {
      concentration.push({
        name: posArray[0].security_name,
        value: totalValue,
        percentOfTotal,
        riskLevel: concentrationRisk,
        portfolios: portfolioIds,
      });
    }

    consolidatedPositions.push(position);
  });

  // Sort by value descending
  consolidatedPositions.sort((a, b) => b.value - a.value);
  concentration.sort((a, b) => b.percentOfTotal - a.percentOfTotal);

  return {
    totalValue: totalAuM,
    positions: consolidatedPositions,
    assetAllocation: allocation.by_asset_class || {},
    geographicExposure: allocation.by_geography || {},
    geographicTiers: allocation.by_geography_tier || {},
    sectorExposure: allocation.by_sector || {},
    currencyExposure: allocation.by_currency || {},
    duplicates,
    concentration,
    allocationTargets: transformAllocationTargets(allocation.targets),
  };
}

function transformAllocationTargets(
  targets: Awaited<ReturnType<typeof portfolioApiService.getAllocation>>['allocation']['targets']
): AllocationTarget[] | undefined {
  if (!targets) return undefined;

  if (Array.isArray(targets)) {
    return targets;
  }

  // Transform Record<string, number> to AllocationTarget[]
  return Object.entries(targets).map(([asset_class, target_weight]) => ({
    asset_class,
    target_weight: target_weight as number,
    current_weight: 0, // Would need current weights
  }));
}

// Exported for test only: the null-vs-false distinction below is a claim about a client's
// portfolio, and it is worth pinning directly rather than through the whole hook.
export function transformDrift(
  driftData: Awaited<ReturnType<typeof portfolioApiService.getDrift>> | undefined,
  driftError: unknown
): DriftAnalysis | null {
  if (driftError) {
    // Return error state indicating missing risk profile
    return {
      driftPercentage: null,
      currentAllocation: {},
      targetAllocation: {},
      driftByAssetClass: {},
      // The branch's own error says the drift CANNOT be calculated, so `false` here asserted
      // "no rebalancing needed" about a portfolio nobody assessed — the same lie the widened
      // API contract exists to prevent, one layer up. `null` is the true answer.
      needsRebalancing: null,
      rebalanceUrgency: null,
      suggestedTrades: [],
      riskProfile: null,
      error: 'Risk profile incomplete - cannot calculate drift',
    };
  }

  if (!driftData) return null;

  const { drift } = driftData;

  return {
    driftPercentage: drift.drift_percentage,
    currentAllocation: drift.current_allocation,
    targetAllocation: drift.target_allocation,
    driftByAssetClass: Object.fromEntries(
      Object.entries(drift.drift_by_asset_class).map(([key, val]) => [
        key,
        {
          current: val.current,
          target: val.target,
          drift: val.drift,
          driftValue: val.drift_value,
          status: val.status,
        },
      ])
    ),
    needsRebalancing: drift.needs_rebalancing,
    rebalanceUrgency: drift.rebalance_urgency,
    suggestedTrades: drift.suggested_trades.map((t) => ({
      assetClass: t.asset_class,
      action: t.action,
      amount: t.amount,
      amountPercentage: t.amount_percentage,
      reason: t.reason,
      priority: t.priority,
    })),
    riskProfile: drift.risk_profile,
  };
}

/**
 * TASK-874: the `by_geography` buckets that count as European exposure outside Switzerland.
 *
 * Canonical display names from `shared/portfolio/country_exposure.py`. This is a hand-kept copy
 * of part of a backend vocabulary and nothing enforces that it stays in step — so it is used
 * only to prove exposure EXISTS, never to prove it is absent. Absence is decided by
 * `hasUnclassifiedGeography` below, which refuses to claim a gap while any bucket is
 * unclassified. A name missing from this list therefore costs a true positive, never a false
 * accusation.
 */
const EUROPE_EX_CH_BUCKETS = [
  'Europe', 'Europe (ex-CH)', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands',
  'Belgium', 'Austria', 'Ireland', 'Luxembourg', 'Portugal', 'Sweden', 'Norway',
  'Denmark', 'Finland', 'United Kingdom', 'Liechtenstein', 'Poland', 'Greece', 'Czechia',
  'Hungary', 'Romania', 'Bulgaria', 'Croatia', 'Slovenia', 'Slovakia', 'Estonia', 'Latvia',
  'Lithuania', 'Cyprus', 'Malta', 'Iceland', 'Serbia', 'Ukraine', 'Jersey', 'Guernsey',
  'Monaco', 'Andorra', 'San Marino',
];

function generateInsights(
  holdings: ConsolidatedHoldings,
  cash: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['cash'],
  totalCashCHF: number,
  totalAuM: number,
  portfolios: Awaited<ReturnType<typeof portfolioApiService.getClientPortfolios>>['portfolios']
): ClientInsights {
  // Build portfolio name lookup
  const portfolioNameMap = new Map<string, string>();
  portfolios.forEach((p) => portfolioNameMap.set(p.id, p.name));

  // Generate concentration risk insights
  const concentrationRisks = holdings.concentration.map((c) => ({
    type: c.portfolios.length > 1 ? 'DUPLICATE_HOLDING' : 'SINGLE_POSITION',
    severity: c.riskLevel,
    title: `${c.name} concentration`,
    description: `${c.percentOfTotal.toFixed(1)}% of total portfolio`,
    value: c.value,
    percentage: c.percentOfTotal,
    recommendation:
      c.riskLevel === 'HIGH'
        ? `Consider reducing ${c.name} exposure to max 20% of total wealth`
        : `Monitor ${c.name} position`,
  }));

  // Calculate cash position
  const cashPercentage = totalAuM > 0 ? (totalCashCHF / totalAuM) * 100 : 0;
  const cashPosition = {
    total: totalCashCHF,
    percentage: cashPercentage,
    breakdown: cash.map((c) => ({
      portfolio: portfolioNameMap.get(c.portfolio_id || c.id) || c.portfolio_id || c.id,
      // TASK-865: was `c.amount_chf || c.amount`. `amount_chf` never existed, so the
      // fallback ALWAYS fired and a USD balance was displayed, and weighted, as CHF.
      amount: c.amount_base ?? 0,
      percentage: totalAuM > 0 ? ((c.amount_base ?? 0) / totalAuM) * 100 : 0,
    })),
  };

  // Identify diversification gaps (simplified)
  const diversificationGaps: ClientInsights['diversificationGaps'] = [];

  // Check for missing geographic exposure.
  //
  // TASK-874: this asked for the keys `Europe` and `Europe (ex-CH)`, and the backend emitted the
  // custodian's raw label — `Europa` — so neither key could ever exist and the gap was raised on
  // every client in the book, including the ones holding half their equity in Europe. The
  // backend now emits one canonical vocabulary (`shared/portfolio/country_exposure.py`), and a
  // German or French holding is European exposure whether or not a `Europe` bucket exists
  // beside it: the region bucket holds only what has no country-level classification.
  const geoExposure = holdings.geographicExposure;
  const geoTiers = holdings.geographicTiers;
  const europeExCh = EUROPE_EX_CH_BUCKETS.reduce(
    (sum, name) => sum + (geoExposure[name] ?? 0),
    0,
  );
  // A bucket the backend vocabulary could not place, or a holding with no geography at all,
  // may BE the European exposure. Telling an advisor "no European exposure" on the strength of
  // a label we failed to read is the confident-wrong-answer this epic exists to remove, so the
  // gap is raised only where the book is fully classified and genuinely holds none.
  const hasUnclassifiedGeography = Object.entries(geoExposure).some(
    ([name, value]) =>
      value > 0 && (geoTiers[name] === 'unresolved' || geoTiers[name] === 'unknown'),
  );
  if (europeExCh <= 0 && !hasUnclassifiedGeography) {
    diversificationGaps.push({
      type: 'GEOGRAPHIC',
      region: 'Europe (ex-CH)',
      currentExposure: 0,
      recommendedExposure: 15,
      description: 'No European exposure outside Switzerland',
    });
  }

  return {
    concentrationRisks,
    cashPosition,
    diversificationGaps,
  };
}

function transformPerformance(
  performance: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['performance'],
  overview: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['overview'],
  positions: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['positions'],
  portfolios: Awaited<ReturnType<typeof portfolioApiService.getClientPortfolios>>['portfolios'],
  totalAuM: number,
  performanceHistory: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['performanceHistory'],
  perPortfolioHistory?: Map<string, any[]>,
  backendContributors?: { top: Array<{ name: string; contribution: number }>; bottom: Array<{ name: string; contribution: number }> },
): PerformanceData {
  // Calculate per-portfolio breakdown from positions
  const portfolioValueMap = new Map<string, { value: number; unrealizedPl: number }>();

  positions.forEach((pos) => {
    const portfolioId = pos.portfolio_id || 'default';
    const existing = portfolioValueMap.get(portfolioId) || { value: 0, unrealizedPl: 0 };
    // TASK-865 review: per-portfolio value must be converted too, or every "% of AuM"
    // computed from it is a converted numerator over a par denominator.
    existing.value += pos.market_value_base ?? 0;
    existing.unrealizedPl += pos.unrealized_pl || 0;
    portfolioValueMap.set(portfolioId, existing);
  });

  // Build per-portfolio performance breakdown
  const byPortfolio = portfolios.map((p) => {
    const data = portfolioValueMap.get(p.id) || { value: 0, unrealizedPl: 0 };
    const contribution = totalAuM > 0 ? (data.unrealizedPl / totalAuM) * 100 : 0;
    const ytdReturn = data.value > 0 ? (data.unrealizedPl / data.value) * 100 : null;

    return {
      id: p.id,
      name: p.name,
      ytd: ytdReturn,
      contributionToTotal: contribution,
      volatility: null,
    };
  });

  // TASK-387: Use backend-calculated contributors (portfolio contribution %)
  // instead of recalculating client-side
  const topContributors = (backendContributors?.top ?? []).map((c) => ({
    name: c.name,
    gain: 0,
    percentage: c.contribution ?? 0,
  }));

  const bottomContributors = (backendContributors?.bottom ?? []).map((c) => ({
    name: c.name,
    contribution: 0,
    percentage: c.contribution ?? 0,
  }));

  // Build per-portfolio date→value lookup maps from real API data
  const portfolioDateMaps: Map<string, number>[] = [];
  portfolios.forEach((p, index) => {
    const dateMap = new Map<string, number>();
    const hist = perPortfolioHistory?.get(p.id);
    if (hist) {
      for (const h of hist) {
        const date = (h.date || h.timestamp || '').slice(0, 10);
        const val = h.value ?? h.return ?? undefined;
        if (date && val !== undefined) {
          dateMap.set(date, val);
        }
      }
    }
    portfolioDateMaps[index] = dateMap;
  });

  const hasPerPortfolioData = portfolioDateMaps.some((m) => m.size > 0);

  // Transform history — merge client total + benchmark + per-portfolio lines
  const history = (performanceHistory || []).map((h: any) => {
    const date = (h.date || h.timestamp || '').slice(0, 10);
    const point: { date: string; value: number; benchmark?: number; [key: string]: any } = {
      date,
      value: h.value || h.return || 0,
      benchmark: h.benchmark || h.benchmark_value || undefined,
    };

    // Inject real per-portfolio values when available
    if (hasPerPortfolioData) {
      portfolioDateMaps.forEach((dateMap, index) => {
        const val = dateMap.get(date);
        if (val !== undefined) {
          point[`portfolio${index + 1}`] = val;
        }
      });
    }

    return point;
  });

  return {
    combined: {
      mtd: null, // Would need MTD calculation
      qtd: null,
      ytd: performance.ytd_return ?? null,
      oneYear: performance.one_year_return ?? null,
      sinceStart: performance.since_inception_return ?? null,
    },
    benchmark: performance.benchmark_return != null ? {
      mtd: null,
      qtd: null,
      ytd: performance.benchmark_return,
      oneYear: null,
      sinceStart: null,
    } : null,
    benchmarkName: performance.benchmark_name ?? null,
    benchmarkCode: performance.benchmark_code ?? null,
    alpha: performance.alpha != null ? {
      mtd: null,
      qtd: null,
      ytd: performance.alpha,
      oneYear: null,
      sinceStart: null,
    } : null,
    byPortfolio,
    topContributors,
    bottomContributors,
    history,
    totalGains: overview.total_unrealized_pl ?? null, // TASK-654: unavailable ≠ 0
  };
}

function transformRisk(
  risk: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['risk'],
  overview: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['overview'],
  positions: Awaited<ReturnType<typeof portfolioApiService.getClientDashboardData>>['positions'],
  portfolios: Awaited<ReturnType<typeof portfolioApiService.getClientPortfolios>>['portfolios'],
  totalAuM: number
): RiskData {
  // Calculate per-portfolio breakdown
  const portfolioValueMap = new Map<string, number>();
  positions.forEach((pos) => {
    const portfolioId = pos.portfolio_id || 'default';
    const existing = portfolioValueMap.get(portfolioId) || 0;
    portfolioValueMap.set(portfolioId, existing + (pos.market_value || 0));
  });

  // Build per-portfolio risk breakdown
  // Note: Real volatility/VaR per portfolio would need historical data per portfolio
  // For now, we approximate based on portfolio weight relative to total
  // TASK-897: null all the way through. These coerced to 0 and were then round-tripped back
  // through `|| null` below — so a GENUINELY zero Sharpe became null and was indistinguishable
  // from one that was never computed. That round trip is why Sharpe read `N/A` on Overview and
  // `0.00` on Risk: two surfaces, one metric, two answers, manufactured here.
  const totalVolatility = risk.volatility ?? overview.volatility ?? null;
  const totalVar1day = risk.var_1day ?? overview.var_1day ?? null;
  const totalSharpe = risk.sharpe_ratio ?? overview.sharpe_ratio ?? null;

  const byPortfolio = portfolios.map((p) => {
    const portfolioValue = portfolioValueMap.get(p.id) || 0;
    const weight = totalAuM > 0 ? portfolioValue / totalAuM : 0;

    // Approximate per-portfolio risk metrics based on weight
    // In reality, these would come from per-portfolio calculations
    return {
      id: p.id,
      name: p.name,
      volatility: weight > 0 ? totalVolatility : null,
      // A share of an unknown is unknown, not zero.
      var1day: weight > 0 && totalVar1day !== null ? totalVar1day * weight : null,
      sharpeRatio: weight > 0 ? totalSharpe : null,
    };
  });

  return {
    combined: {
      // `?? null`, not `|| null`: a measured 0 is a fact, and `||` erased it.
      volatility: totalVolatility ?? null,
      var1day: totalVar1day ?? null,
      sharpeRatio: totalSharpe ?? null,
      maxDrawdown: null, // Would need drawdown calculation
      beta: risk.beta ?? null,
    },
    byPortfolio,
  };
}
